import React from "react";
import { toast } from "sonner";
import { Award, CheckCircle2, Briefcase, Workflow, MessageSquare, FileDown, Star, StarOff, Sparkles, Crown, Video, Tag, CalendarClock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function CandidateCard({ candidate, isShortlisted, onToggleShortlist, onContact, onInviteInterview, onScheduleMeet, selectable, selected, onToggleSelect }) {
  const c = candidate;

  const downloadResume = async () => {
    try {
      const res = await api.get(`/recruiter/resume/${c.user_id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${c.name.replace(/\s/g, "_")}_Resume.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error(`${c.name} has disabled resume downloads`);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to download resume");
      }
    }
  };
  return (
    <div className="glass-panel rounded-2xl p-5 relative" data-testid={`candidate-card-${c.user_id}`}>
      {selectable && (
        <input
          type="checkbox" checked={!!selected} onChange={() => onToggleSelect(c.user_id)}
          data-testid={`candidate-select-checkbox-${c.user_id}`}
          className="absolute top-4 right-4 w-4 h-4 accent-indigo-400 cursor-pointer"
        />
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-400/30 to-purple-500/30 flex items-center justify-center font-heading font-bold text-white shrink-0">
            {c.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div>
            <p className="text-sm font-semibold text-white flex items-center gap-1.5">
              {c.name}
              {c.is_premium && <Crown className="w-3.5 h-3.5 text-amber-400" title="Premium member" data-testid={`candidate-premium-badge-${c.user_id}`} />}
            </p>
            <p className="text-xs text-slate-400">{c.level_info?.title} · Level {c.level_info?.level} · {c.xp} XP{c.dominant_track ? ` · ${c.dominant_track.replace("_", " ")}` : ""}</p>
          </div>
        </div>
        {c.match_score != null && (
          <span className="flex items-center gap-1 text-xs font-bold text-emerald-400 shrink-0" data-testid={`candidate-match-score-${c.user_id}`}>
            <Sparkles className="w-3.5 h-3.5" /> {c.match_score}% Match
          </span>
        )}
      </div>

      {c.reasoning && <p className="text-xs text-slate-400 mt-3 italic leading-relaxed">"{c.reasoning}"</p>}

      <div className="flex flex-wrap gap-2 mt-3">
        <span className="flex items-center gap-1 text-[11px] font-semibold bg-white/5 border border-white/10 rounded-full px-2.5 py-1 text-slate-300">
          <CheckCircle2 className="w-3 h-3 text-indigo-400" /> {c.completed_challenges}/30 Challenges
        </span>
        {c.case_studies_completed > 0 && (
          <span className="flex items-center gap-1 text-[11px] font-semibold bg-white/5 border border-white/10 rounded-full px-2.5 py-1 text-slate-300">
            <Briefcase className="w-3 h-3 text-purple-300" /> {c.case_studies_completed} Case Studies ({c.case_studies_avg_score}%)
          </span>
        )}
        {c.simulator_completed > 0 && (
          <span className="flex items-center gap-1 text-[11px] font-semibold bg-white/5 border border-white/10 rounded-full px-2.5 py-1 text-slate-300">
            <Workflow className="w-3 h-3 text-emerald-300" /> {c.simulator_completed} Simulations
          </span>
        )}
        {c.has_certificate && (
          <span className="flex items-center gap-1 text-[11px] font-semibold bg-[#FBBF24]/10 border border-[#FBBF24]/30 rounded-full px-2.5 py-1 text-[#FBBF24]">
            <Award className="w-3 h-3" /> Certified ({c.certificate_score}%)
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {c.skills?.slice(0, 4).map((s) => (
          <span key={s} className="text-[10px] text-slate-400 bg-white/[0.03] rounded px-2 py-1">{s}</span>
        ))}
      </div>

      {c.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid={`candidate-tags-${c.user_id}`}>
          {c.tags.map((t) => (
            <span key={t} className="flex items-center gap-1 text-[10px] font-semibold text-indigo-300 bg-indigo-400/10 border border-indigo-400/20 rounded-full px-2 py-0.5">
              <Tag className="w-2.5 h-2.5" /> {t}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5 flex-wrap">
        <button
          onClick={() => onToggleShortlist(c)} data-testid={`candidate-shortlist-btn-${c.user_id}`}
          className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${isShortlisted ? "bg-[#FBBF24]/15 text-[#FBBF24]" : "bg-white text-slate-900 hover:bg-slate-200"}`}
        >
          {isShortlisted ? <Star className="w-3.5 h-3.5" /> : <StarOff className="w-3.5 h-3.5" />} {isShortlisted ? "Shortlisted" : "Shortlist"}
        </button>
        <button
          onClick={() => onContact(c)} data-testid={`candidate-contact-btn-${c.user_id}`}
          className="flex items-center gap-1.5 text-xs font-semibold bg-white hover:bg-slate-200 text-slate-900 px-3 py-2 rounded-lg transition-colors"
        >
          <MessageSquare className="w-3.5 h-3.5" /> Contact
        </button>
        {onInviteInterview && (
          <button
            onClick={() => onInviteInterview(c)} data-testid={`candidate-invite-interview-btn-${c.user_id}`}
            className="flex items-center gap-1.5 text-xs font-semibold bg-purple-400/10 hover:bg-purple-400/20 text-purple-300 px-3 py-2 rounded-lg transition-colors"
          >
            <Video className="w-3.5 h-3.5" /> Invite to Interview
          </button>
        )}
        {onScheduleMeet && (
          <button
            onClick={() => onScheduleMeet(c)} data-testid={`candidate-schedule-meet-btn-${c.user_id}`}
            className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-300 px-3 py-2 rounded-lg transition-colors"
          >
            <CalendarClock className="w-3.5 h-3.5" /> Schedule Meet
          </button>
        )}
        {c.resume_downloadable !== false && (
          <button
            onClick={downloadResume} data-testid={`candidate-resume-btn-${c.user_id}`}
            className="flex items-center gap-1.5 text-xs font-semibold bg-white hover:bg-slate-200 text-slate-900 px-3 py-2 rounded-lg transition-colors ml-auto"
          >
            <FileDown className="w-3.5 h-3.5" /> Resume
          </button>
        )}
      </div>
    </div>
  );
}
